import { useCallback, useMemo, useRef, useState } from "react";

import { useMedplum } from "@medplum/react";

import { fetchIsLabOrdersIntegrationActive } from "@/health-gorilla";
import { useDestinationAccess } from "@/navigation";
import { captureExceptionWithoutPhi } from "@/utils/sentry";

import type { OmniSearchEntry } from "../types/omni-search";
import { buildSearchIndex } from "../utils/build-search-index";
import { matchSearchEntries } from "../utils/match-search-entries";

type UseOmniSearchEntriesResult = {
  /** The pages that match the query, best matches first. */
  entries: OmniSearchEntry[];
  /** Looks up whether lab orders are active. Safe to call on every open: it runs once. */
  loadLabsIntegration: () => void;
};

/**
 * Gives the search results for a query, limited to the pages the current user can open.
 *
 * The lab-integration lookup is not made on mount. The caller runs `loadLabsIntegration` when the
 * search first opens. The answer is kept in this hook's state, and the header that owns the hook
 * stays mounted across routes, so the lookup runs once per page load. Until it resolves, or if it
 * fails, Lab Order Templates stays hidden. A failure goes to Sentry through
 * `captureExceptionWithoutPhi`, with the practice id as the only context.
 *
 * @param query - The text the user typed. It is only used to match; it is never logged or reported.
 * @returns The matching entries and the function that starts the lab-integration lookup.
 */
export function useOmniSearchEntries(query: string): UseOmniSearchEntriesResult {
  const medplum = useMedplum();
  const [isLabOrdersIntegrationActive, setIsLabOrdersIntegrationActive] = useState<
    boolean | undefined
  >(undefined);
  const hasRequestedLabsIntegrationRef = useRef(false);
  const access = useDestinationAccess(isLabOrdersIntegrationActive);

  const { practiceId } = access;

  const searchIndex = useMemo(() => buildSearchIndex(access), [access]);
  const entries = useMemo(() => matchSearchEntries(searchIndex, query), [searchIndex, query]);

  const loadLabsIntegration = useCallback(() => {
    if (hasRequestedLabsIntegrationRef.current) return;
    hasRequestedLabsIntegrationRef.current = true;

    fetchIsLabOrdersIntegrationActive(medplum, practiceId)
      .then(setIsLabOrdersIntegrationActive)
      .catch((error: unknown) =>
        captureExceptionWithoutPhi("Omni-search lab integration lookup failed", error, {
          practiceId,
        }),
      );
  }, [medplum, practiceId]);

  return { entries, loadLabsIntegration };
}
